import React, { useState, useEffect } from 'react';
import { Container, Tabs, Tab, Row, Col, Breadcrumb, Card, Badge } from 'react-bootstrap';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import IRMList from './IRMList';
import KnockOffTable from './KnockOffTable';
import Base from '../Base';
import instance from "../../services/baseService";
import toast from "react-hot-toast";

const IRMDetails = () => {
    const [activeTab, setActiveTab] = useState('irm-list');
    const [irmList, setIrmList] = useState([]);
    const [irmPeriod, setIrmPeriod] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [fccFilter, setFccFilter] = useState('');
    const [availableFccs, setAvailableFccs] = useState([]);
    const [loadingAction, setLoadingAction] = useState(false);
    const { client_id, period_id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        fetchIRMList();
    }, [page, perPage, statusFilter, fccFilter]);

    const fetchIRMList = async () => {
        try {
            setLoading(true);
            setError(null);

            const payload = {
                page: page,
                perpage: perPage,
            };

            if (searchTerm) {
                payload.search = searchTerm;
            }

            if (statusFilter !== null) {
                payload.status = statusFilter;
            }

            if (fccFilter) {
                payload.fcc = fccFilter;
            }

            const response = await instance.post(
                `/api/v1/ebrc/irm/list/${client_id}/${period_id}`,
                payload
            );

            if (response.data.type === 'success') {
                setIrmList(response.data.data.lists || []);
                setIrmPeriod(response.data.data.irm_period || null);
                setSummary(response.data.data.summary || null);
                setTotalCount(response.data.data.total_count || 0);
                setTotalPages(response.data.data.total_pages || 0);
                setAvailableFccs(response.data.data.available_fccs || []);
            } else {
                setError(response.data.message || 'Failed to load IRM list');
            }
        } catch (err) {
            console.error('Error loading IRM list:', err);
            setError(err.response?.data?.message || 'Error loading IRM list: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (newTab) => {
        setActiveTab(newTab);

        // If switching back to IRM List tab, refresh data with initial states
        if (newTab === 'irm-list') {
            handleClearFilters();
        }
    };

    const handleSearch = () => {
        setPage(1); // Reset to first page
        fetchIRMList();
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleStatusFilterChange = (status) => {
        setStatusFilter(status);
        setPage(1); // Reset to first page
    };

    const handleFccFilterChange = (fcc) => {
        setFccFilter(fcc);
        setPage(1); // Reset to first page
    };

    // ADD THESE HANDLER FUNCTIONS
    const handleApprove = async (irmId) => {
        try {
            setLoadingAction(true);
            const response = await instance.post(
                `/api/v1/ebrc/irm/period/approve/${client_id}/${irmId}`
            );

            if (response.data.type === 'success') {
                toast.success('IRM approved successfully');
                fetchIRMList(); // Refresh the list
            } else {
                toast.error(response.data.message || 'Failed to approve IRM');
            }
        } catch (err) {
            console.error('Error approving IRM:', err);
            toast.error(err.response?.data?.message || 'Error approving IRM');
        } finally {
            setLoadingAction(false);
        }
    };

    const handleReject = async (irmId) => {
        try {
            setLoadingAction(true);
            const response = await instance.post(
                `/api/v1/ebrc/irm/period/reject/${client_id}/${irmId}`
            );

            if (response.data.type === 'success') {
                toast.success('IRM rejected successfully');
                fetchIRMList(); // Refresh the list
            } else {
                toast.error(response.data.message || 'Failed to reject IRM');
            }
        } catch (err) {
            console.error('Error rejecting IRM:', err);
            toast.error(err.response?.data?.message || 'Error rejecting IRM');
        } finally {
            setLoadingAction(false);
        }
    };


    const handleClearFilters = async () => {
        try {
            setLoading(true);
            setError(null);

            // Reset state
            setSearchTerm('');
            setFccFilter('');
            setStatusFilter(null);
            setPage(1);

            // Fetch with cleared filters directly
            const payload = {
                page: 1,
                perpage: perPage,
                // No search, no fcc, no status filters
            };

            const response = await instance.post(
                `/api/v1/ebrc/irm/list/${client_id}/${period_id}`,
                payload
            );

            if (response.data.type === 'success') {
                setIrmList(response.data.data.lists || []);
                setIrmPeriod(response.data.data.irm_period || null);
                setSummary(response.data.data.summary || null);
                setTotalCount(response.data.data.total_count || 0);
                setTotalPages(response.data.data.total_pages || 0);
                setAvailableFccs(response.data.data.available_fccs || []);
            } else {
                setError(response.data.message || 'Failed to load IRM list');
            }
        } catch (err) {
            console.error('Error loading IRM list:', err);
            setError(err.response?.data?.message || 'Error loading IRM list: ' + err.message);
        } finally {
            setLoading(false);
        }
    };


    return (
        <Base>
            <Container fluid className="p-4 bg-light min-vh-100">
                <Row>
                    <Col>
                        <Breadcrumb>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/" }}>
                                Dashboard
                            </Breadcrumb.Item>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/clients" }}>
                                Clients
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                linkAs={NavLink}
                                linkProps={{ to: `/clients/tax-forms/${client_id}` }}
                            >
                                Tax-Forms
                            </Breadcrumb.Item>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: `/ebrc/${client_id}` }}>
                                EBRC
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>IRM Period Details</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>

                {/* Period Info Card */}
                {irmPeriod && (
                    <Row className="mb-3">
                        <Col>
                            <Row className="align-items-center ">
                                <Col className="bg-white p-4">
                                    <h4 className=" fw-bold mb-1">
                                        IRM Management System
                                    </h4>
                                    <h6 className="text-muted mb-2">
                                        Period: {irmPeriod.start_date} to {irmPeriod.end_date}
                                    </h6>
                                    <h6 className=" mb-2 bg-green-100 text-green-700 px-2 py-1 font-bold d-inline-block">
                                        eBRC Generation Status :
                                        <span className="ms-1">
                                            Pending ({irmPeriod.pending_brc_count})
                                        </span>
                                    </h6>

                                    <h6 className="bg-green-100 text-green-700 px-2 py-1  font-bold d-inline-block">
                                        Certificate Generation Status :
                                        <span className="ms-1">
                                            Pending ({irmPeriod.pending_certificate_count})
                                        </span>
                                    </h6>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                )}
                <Row>
                    <Col>
                        <div className="bg-white p-4">
                            <Tabs
                                activeKey={activeTab}
                                onSelect={handleTabChange}
                                className="mb-4 custom-tabs scrollable-tabs"
                                id="irm-tabs"
                            >
                                <Tab eventKey="irm-list" title={`IRM List`}>
                                    <IRMList
                                        irmPeriod={irmPeriod}
                                        irmList={irmList}
                                        loading={loading}
                                        error={error}
                                        page={page}
                                        perPage={perPage}
                                        totalCount={totalCount}
                                        totalPages={totalPages}
                                        searchTerm={searchTerm}
                                        statusFilter={statusFilter}
                                        fccFilter={fccFilter}
                                        availableFccs={availableFccs}
                                        onPageChange={handlePageChange}
                                        onSearchChange={setSearchTerm}
                                        onSearch={handleSearch}
                                        onStatusFilterChange={handleStatusFilterChange}
                                        onFccFilterChange={handleFccFilterChange}
                                        summary={summary}
                                        onApprove={handleApprove}
                                        onReject={handleReject}
                                        loadingAction={loadingAction}
                                        onClearFilters={handleClearFilters}
                                    />
                                </Tab>
                                {
                                    irmPeriod && irmPeriod.approved === 1 && (
                                        <Tab eventKey="knox-table" title="Knock-Off Table">
                                            <div className="tab-content-container">
                                                <KnockOffTable />
                                            </div>
                                        </Tab>
                                    )
                                }
                            </Tabs>
                        </div>
                    </Col>
                </Row>
            </Container>
        </Base>
    );
};

export default IRMDetails;